// Owl Hour Flow Section — "How OWL HOUR works" + region coverage.
// 2-column grid matching prototype: owl-flow (left) + owl-region (right).

import type { League } from "../../leagues/useLeagues.ts";
import type { ParsedContinentFlags } from "../../owl/owlContinentFlags.ts";
import { OwlContinentFlagRows } from "../../components/owl-extras/OwlContinentFlagRows.tsx";

// ─── Sleep-to-win flow steps ─────────────────────────────────

const FLOW_STEPS = [
  { time: "12:00", name: "First slate", desc: "Today's picks available until the evening refresh" },
  { time: "17:00", name: "Evening refresh", desc: "Fresh picks include matches after midnight" },
  { time: "19:00–07:00", name: "Session live", desc: "Matches play through the Accra night · auto-graded" },
  { time: "Morning", name: "Verified results", desc: "Late fixtures may still be playing at 07:00" },
] as const;

// ─── Region coverage ─────────────────────────────────────────

const REGIONS = [
  { flag: "🇯🇵", name: "Japan", sub: "J.League · qualifying morning kickoffs" },
  { flag: "🇦🇺", name: "Australia", sub: "A-League · qualifying morning kickoffs" },
  { flag: "🇨🇳", name: "China", sub: "Qualifying early kickoffs" },
  { flag: "🇨🇦", name: "Canada", sub: "CPL · MLS" },
  { flag: "🌍", name: "Europe & beyond", sub: "Qualifying evening fixtures" },
  { flag: "🇦🇷", name: "Argentina", sub: "Primera División · Copa Argentina" },
  { flag: "🇲🇽", name: "Mexico", sub: "Liga MX · Liga de Expansión" },
  { flag: "🇺🇸", name: "USA", sub: "MLS · USL Championship" },
  { flag: "🇧🇷", name: "Brazil", sub: "Brasileirão · Copa do Brasil" },
  { flag: "🇨🇴", name: "Colombia", sub: "Liga BetPlay" },
  // GAP 5 — Peru and Chile shown as separate rows (AC-5.1 / AC-5.2 / AC-5.3).
  { flag: "🇵🇪", name: "Peru", sub: "Liga 1" },
  { flag: "🇨🇱", name: "Chile", sub: "Primera División" },
] as const;

// ─── Component ────────────────────────────────────────────────

export interface OwlFlowSectionProps {
  leagues: readonly League[];
  /** Continent flags from owl_hour_slips.continent_flags JSONB (GAP 2). */
  continentFlags?: ParsedContinentFlags;
}

export function OwlFlowSection({ leagues, continentFlags }: OwlFlowSectionProps): JSX.Element {
  const activeCount = leagues.filter((l) => l.status === "active").length;

  return (
    <div className="owl-grid">
      {/* ── How it works ──────────────────────── */}
      <div className="owl-flow">
        <div className="h">
          How OWL HOUR works
          <span className="badge">SLEEP-TO-WIN</span>
        </div>
        <div className="steps">
          {FLOW_STEPS.map((step, i) => (
            <div key={i} className="owl-step">
              <div className="time">{step.time}</div>
              <div className="name">{step.name}</div>
              <div className="desc">{step.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Region coverage ───────────────────── */}
      <div className="owl-region">
        <div className="h">
          Coverage · {activeCount > 0 ? activeCount : REGIONS.length} regions tonight
        </div>
        {REGIONS.map((r, i) => (
          <div key={i} className="owl-region-row" data-testid="owl-region-row">
            <div className="flag">{r.flag}</div>
            <div className="info">
              <div className="name">{r.name}</div>
              <div className="sub">{r.sub}</div>
            </div>
          </div>
        ))}
        {/* GAP 2 — Altitude + Rivalry flag rows from continent_flags JSONB. */}
        {continentFlags !== undefined ? (
          <OwlContinentFlagRows flags={continentFlags} />
        ) : null}
      </div>
    </div>
  );
}
